<template>
    <div>
        <CalculationOverlay v-if="showCalculatingOverlay"
                            :failed="calculationFailed"
                            :retrying="retrying"
                            @retry="retryWithLlm"
                            @calculate-without-llm="calculateWithoutLlm"
                            @close="closeOverlay"/>
        <div v-if="aiFallbackUsed" class="alert alert-warning d-flex align-items-start gap-3 mb-3">
            <span class="fas fa-exclamation-triangle mt-1"></span>
            <div>
                <strong>AI výběr srovnávacích nemovitostí nebyl použit.</strong>
                Pro spuštění AI Asistenta vám nezbývají AI kredity, proto byl pro tento výpočet použit standardní algoritmus. Doplnit kredity můžete v sekci Nákup AI kreditů.
            </div>
        </div>
        <DescriptionSourcesPanel v-if="created"
                                 ref="sourcesPanel"
                                 :sources-client="sourcesClient"
                                 :initial-attachments="estimate.attachments"
                                 :load-attachments="loadAttachments"
                                 @sources-changed="onSourcesChanged"
                                 @show-found-values="openFoundValues"/>
        <FoundValuesDialog v-if="created"
                           ref="foundValuesDialog"
                           :save-handler="saveFoundValues"
                           @closed="onFoundValuesClosed"
                           @show-segment="path => estimateForm?.showSegment(path)"/>
        <div v-if="foundToastShown" class="toast-container position-fixed bottom-0 end-0 p-3">
            <div class="toast show" role="status">
                <div class="toast-body d-flex align-items-center gap-3">
                    <span class="fas fa-check-circle text-success"></span>
                    <div class="flex-grow-1">
                        Podklady jsou zpracované, {{ foundCounts.new }}
                        {{ plural(foundCounts.new, 'nová hodnota', 'nové hodnoty', 'nových hodnot') }}.
                    </div>
                    <button type="button" class="btn btn-sm btn-primary" @click="openFoundValues(null)">Zobrazit</button>
                    <button type="button" class="btn-close" aria-label="Zavřít" @click="foundToastShown = false"></button>
                </div>
            </div>
        </div>
        <EstimateForm ref="estimateForm"
                      @save="onSegmentSave"
                      :estimate="estimate"
                      :segment-configurations-factory="createSegmentConfiguration">
            <template v-if="created" #actions>
                <div class="d-flex gap-2">
                <button v-if="foundCounts.total > 0" type="button"
                        class="btn d-inline-flex align-items-center px-3 text-nowrap"
                        :class="foundCounts.new > 0 ? 'btn-primary' : 'btn-outline-primary description-sources-button'"
                        @click="openFoundValues(null)">
                    Hodnoty z podkladů
                    <span v-if="foundCounts.new > 0" class="badge rounded-pill text-bg-light ms-2">{{ foundCounts.new }} nové</span>
                    <span v-else class="badge rounded-pill text-bg-primary ms-2">{{ foundCounts.total }}</span>
                </button>
                <button type="button"
                        class="btn btn-outline-primary description-sources-button d-inline-flex align-items-center px-3 text-nowrap"
                        @click="sourcesPanel?.show()">
                    <span class="fas fa-magic me-2"></span>Vyplnit s pomocí AI
                    <span v-if="sourceCount > 0" class="badge rounded-pill text-bg-primary ms-2">{{ sourceCount }}</span>
                </button>
                </div>
            </template>
        </EstimateForm>
        <RecalculationDialog :recalculation-handler="() => model.recalculateWithNewEstates()"
                             ref="recalculationDialog"/>
    </div>
</template>

<script lang="ts">
import {Attachment, ExpertEstimateState as EstimateState} from '@/model/api';
import {ParameterList, ParameterValues} from '@/model/api/parameters';
import EstimateFoundValues from '@/model/estimates/EstimateFoundValues';
import {ErrorWithUserMessage} from '@/model/errors/ErrorWithUserMessage';
import EstimateDescriptionSources, {DescriptionSource} from '@/model/estimates/EstimateDescriptionSources';
import ExpertEstimate from '@/model/estimates/ExpertEstimate';
import {HttpClient} from '@/model/http';
import {NotSubmittedParameters, OfflineManager, StagedParameters} from '@/model/offline';
import {StoredParameters} from '@/model/offline/storage/StoredParameters';
import {isLeft} from 'fp-ts/lib/Either';
import {fromPairs, isEqual} from 'lodash-es';
import {computed, defineComponent, PropType, provide, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import CalculationOverlay from './expertEstimate/CalculationOverlay.vue';
import DescriptionSourcesPanel from './expertEstimate/DescriptionSourcesPanel.vue';
import {buildFoundRows, describeDifferences, FoundParameter} from './expertEstimate/foundValues';
import FoundValuesDialog, {MissingParameters} from './expertEstimate/FoundValuesDialog.vue';
import {plural} from './common/photos/texts';
import EstimateForm from './common/EstimateForm.vue';
import PhotosSegment from './common/PhotosSegment.vue';
import {LocationSegment, ParametersSegment, SegmentConfiguration} from './common/segments';
import {FoundDifferences, PARAMETER_FOUND_VALUES, ParameterFoundValues} from './common/segments/ParameterFoundValues';
import AttachmentsSegment from './common/segments/AttachmentsSegment.vue';
import RecalculationDialog from './realEstateEstimate/RecalculationDialog.vue';

export default defineComponent({
    components: {EstimateForm, RecalculationDialog, CalculationOverlay, DescriptionSourcesPanel, FoundValuesDialog},
    props: {
        estimate: {
            type: Object as PropType<EstimateState>,
            required: true,
        },
    },
    setup(props) {
        const {t} = useI18n();

        const showCalculatingOverlay = ref(false);
        const calculationFailed = ref(false);
        const retrying = ref(false);
        const aiFallbackUsed = ref(false);

        const recalculationDialog = ref<InstanceType<typeof RecalculationDialog> | null>(null);
        const sourcesPanel = ref<InstanceType<typeof DescriptionSourcesPanel> | null>(null);

        // the sources need a saved estimate, it is saved with its address
        const created = ref(props.estimate.created);
        const sourceCount = ref(0);
        const sourcesClient = new EstimateDescriptionSources(props.estimate.id, new HttpClient());

        const estimateForm = ref<InstanceType<typeof EstimateForm> | null>(null);
        const foundValuesDialog = ref<InstanceType<typeof FoundValuesDialog> | null>(null);
        const foundValuesClient = new EstimateFoundValues(props.estimate.id, new HttpClient());
        const found = ref<FoundParameter[]>([]);
        const sourceNames = ref<Record<string, string>>({});
        const foundToastShown = ref(false);
        // the saved estimate the found values are compared with
        const formState = ref<EstimateState>(props.estimate);
        let sourcesProcessing = false;
        let recalculationPending = false;

        const notSubmittedParameters = new NotSubmittedParameters(
            new StoredParameters(
                props.estimate.id,
                NotSubmittedParameters.STORAGE_KEY,
            ),
        );

        const model = new ExpertEstimate(props.estimate, new HttpClient());

        const offlineManager = new OfflineManager<EstimateState>(
            new StagedParameters(
                new StoredParameters(
                    props.estimate.id,
                    StagedParameters.STORAGE_KEY,
                ),
            ),
            model,
        );

        provide('notSubmittedParameters', notSubmittedParameters);
        provide('model', model);
        provide('offlineManager', offlineManager);

        model.onCalculatingStart(() => {
            showCalculatingOverlay.value = true;
            calculationFailed.value = false;
        });

        model.onCalculatingFailed(() => {
            calculationFailed.value = true;
        });

        model.onCalculatingError(() => {
            showCalculatingOverlay.value = false;
        });

        model.onAiFallback(() => {
            aiFallbackUsed.value = true;
        });

        async function retryWithLlm(): Promise<void> {
            retrying.value = true;
            calculationFailed.value = false;

            const response = await model.createEstimate();

            if (isLeft(response)) {
                retrying.value = false;
                return;
            }

            window.location.href = response.right;
        }

        async function calculateWithoutLlm(): Promise<void> {
            retrying.value = true;

            const response = await model.createEstimateWithoutLlm();

            if (isLeft(response)) {
                retrying.value = false;
                calculationFailed.value = true;
                return;
            }

            window.location.href = response.right;
        }

        function closeOverlay(): void {
            showCalculatingOverlay.value = false;
            calculationFailed.value = false;
        }

        function createSegmentConfiguration(estimate: EstimateState): SegmentConfiguration[] {
            return [
                {
                    label: t('form.segments.location'),
                    path: '/adresa',
                    component: LocationSegment,
                    props: {defaultValue: estimate.location},
                },
                ...estimate.segments.map(segment => ({
                    label: segment.label,
                    path: '/' + segment.path,
                    component: ParametersSegment,
                    props: {
                        parameters: Object.fromEntries(
                            segment.parameterNames.map(name => [name, estimate.parameters[name]]),
                        ),
                        stagedParameters: offlineManager.stagedParameters,
                        notSubmittedParameters,
                    },
                })),
                {
                    label: t('form.segments.photos'),
                    path: '/fotografie',
                    component: PhotosSegment,
                    props: {photos: estimate.photos, estimate},
                },
                {
                    label: t('form.segments.attachments'),
                    path: '/prilohy',
                    component: AttachmentsSegment,
                    props: {
                        availableAttachmentTypes: estimate.availableAttachmentTypes,
                        enabledAttachmentTypes: estimate.enabledAttachmentTypes,
                        attachments: estimate.attachments,
                        estimate,
                    },
                },
            ];
        }

        /**
         * The values of the form, also those not saved yet
         */
        function formValues(state: EstimateState): ParameterValues {
            return {
                ...Object.fromEntries(Object.entries(state.parameters).map(([name, definition]) => [name, definition.value])),
                ...notSubmittedParameters.get(),
            };
        }

        const foundCounts = computed(() => {
            const rows = buildFoundRows(found.value, formState.value.parameters, formValues(formState.value), null).rows;

            return {total: rows.length, new: rows.filter(row => row.isNew).length};
        });

        async function loadFoundValues(): Promise<void> {
            try {
                found.value = await foundValuesClient.load();
            } catch {
                // the button shows what was loaded before
            }
        }

        async function onSourcesChanged(sources: DescriptionSource[], names: Record<string, string>): Promise<void> {
            sourceCount.value = sources.length;
            sourceNames.value = names;

            const wasProcessing = sourcesProcessing;
            sourcesProcessing = sources.some(source => source.state === 'processing');

            if (sourcesProcessing) {
                return;
            }

            // the values come from the processed sources only
            if (sources.some(source => source.state === 'completed')) {
                await loadFoundValues();
            } else {
                found.value = [];
            }

            if (!wasProcessing || foundCounts.value.new === 0) {
                return;
            }

            // the user waiting in the panel sees the values at once, the one filling the form is only told
            if (sourcesPanel.value?.isShown()) {
                sourcesPanel.value.hide();
                await openFoundValues(null);
            } else {
                foundToastShown.value = true;
            }
        }

        // the card of a parameter tells the values of the sources that differ from its field
        provide<ParameterFoundValues>(PARAMETER_FOUND_VALUES, {
            describeDifferences(name: string, value: any): FoundDifferences {
                const parameter = found.value.find(foundParameter => foundParameter.name === name);

                return parameter === undefined
                    ? {count: 0, kind: 'values'}
                    : describeDifferences(parameter, formState.value.parameters, value);
            },
            show: (name: string) => void openFoundValues(null, name),
        });

        /**
         * @param parameterName shows the values of this parameter only
         */
        async function openFoundValues(sourceId: string | null, parameterName: string | null = null): Promise<void> {
            foundToastShown.value = false;
            sourcesPanel.value?.hide();

            const [state] = await Promise.all([model.getEstimate(), loadFoundValues()]);
            formState.value = state;

            foundValuesDialog.value?.show({
                found: found.value,
                definitions: state.parameters,
                current: formValues(state),
                segments: state.segments,
                sourceNames: sourceNames.value,
            }, sourceId, parameterName);
        }

        /**
         * The required parameters still empty by the segments of the form, the values not saved yet count as filled in
         */
        function missingParameters(state: EstimateState): MissingParameters[] {
            const values = formValues(state);

            return state.segments
                .map(segment => ({
                    label: segment.label,
                    path: '/' + segment.path,
                    names: segment.parameterNames.filter(name => state.parameters[name] !== undefined
                        && !state.parameters[name].optional
                        && (values[name] === null || values[name] === undefined)),
                }))
                .filter(segment => segment.names.length > 0);
        }

        async function saveFoundValues(
            values: ParameterValues,
            names: string[],
        ): Promise<{error: string} | {missing: MissingParameters[]}> {
            const before = await model.getEstimate();

            try {
                const state = await foundValuesClient.takeOver(values);

                // the values taken over replace those not saved yet, the other values not saved yet stay,
                // the segment puts them over the saved values again
                notSubmittedParameters.remove(names);
                formState.value = state;
                estimateForm.value!.applyEstimate(state);
                recalculationPending = recalculationAvailable(1, state, before);

                return {missing: missingParameters(state)};
            } catch (error) {
                return {
                    error: error instanceof ErrorWithUserMessage
                        ? error.message
                        : 'Hodnoty se nepodařilo uložit, zkuste to prosím znovu.',
                };
            }
        }

        async function onFoundValuesClosed(sourceIds: string[]): Promise<void> {
            if (recalculationPending) {
                recalculationPending = false;
                recalculationDialog.value!.show();
            }

            try {
                if (sourceIds.length > 0) {
                    await foundValuesClient.markAsSeen(sourceIds);
                }
            } finally {
                await loadFoundValues();
            }
        }

        async function loadAttachments(): Promise<Attachment[]> {
            return (await model.getEstimate()).attachments;
        }

        function onSegmentSave(segmentIndex: number, state: EstimateState, lastKnownState: EstimateState): void {
            created.value = state.created;
            formState.value = state;

            if (!recalculationAvailable(segmentIndex, state, lastKnownState)) {
                return;
            }

            recalculationDialog.value!.show();
        }

        function recalculationAvailable(
            segmentIndex: number, state: EstimateState, lastKnownState: EstimateState,
        ): boolean {
            if (state.calculationUrl === null) {
                return false;
            }

            if (segmentIndex === 0) {
                return !isEqual(state.location, lastKnownState.location);
            }

            return !isEqual(getRequiredParameters(state), getRequiredParameters(lastKnownState));
        }

        function getRequiredParameters(estimate: EstimateState): ParameterList {
            return fromPairs(
                Object.entries(estimate.parameters).filter(([, definition]) => !definition.optional),
            );
        }

        return {
            showCalculatingOverlay,
            calculationFailed,
            retrying,
            aiFallbackUsed,
            recalculationDialog,
            sourcesPanel,
            created,
            sourceCount,
            sourcesClient,
            loadAttachments,
            onSourcesChanged,
            estimateForm,
            foundValuesDialog,
            foundToastShown,
            foundCounts,
            openFoundValues,
            saveFoundValues,
            onFoundValuesClosed,
            plural,
            model,
            retryWithLlm,
            calculateWithoutLlm,
            closeOverlay,
            createSegmentConfiguration,
            onSegmentSave,
        };
    },
});
</script>
